import { useState } from 'react';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import { useModalDialog } from '../../../common/hooks/use-modal-dialog';
import { useAuth } from '../../../global/contexts/auth/auth';
import { useCreateUser, useUpdateUser } from '../contexts/users';
import { ROLE_LABEL, assignableRoles } from '../utils/roles';

import './styles/form.css';

// Alta/edición de un usuario staff (admin-users.md). ALTA: name/email/password/role/status
// (POST, users:write). EDICIÓN: name/email/status (PATCH; el rol NO se toca acá — va por
// PATCH /:id/role, y el password no se edita). Los roles ofrecidos se acotan a ≤ el propio
// (guard de jerarquía Q-169); el backend igual lo valida (403).
export default function UserForm({ user, onClose, onSaved }) {
    const editing = !!user;
    const { permissions } = useAuth();
    const roles = assignableRoles(permissions?.role);

    const { mutateAsync: create, isPending: creating } = useCreateUser();
    const { mutateAsync: update, isPending: updating } = useUpdateUser(user?.id);
    const busy = creating || updating;
    const cardRef = useModalDialog(onClose);

    const [name, setName] = useState(user?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [password, setPassword] = useState('');
    const [role, setRole] = useState(user?.role || (roles.includes('technician') ? 'technician' : roles[roles.length - 1] || 'owner'));
    const [status, setStatus] = useState(user?.status ?? 2);

    const submit = async () => {
        if (!name.trim()) { toast.error('El nombre es obligatorio'); return; }
        if (!email.trim()) { toast.error('El correo es obligatorio'); return; }
        if (!editing && (password.length < 10 || password.length > 100)) { toast.error('La contraseña debe tener entre 10 y 100 caracteres'); return; }
        try {
            if (editing) {
                await update({ name: name.trim(), email: email.trim().toLowerCase(), status: Number(status) });
                toast.success('Usuario actualizado');
            } else {
                await create({ name: name.trim(), email: email.trim().toLowerCase(), password, role, status: Number(status) });
                toast.success('Usuario creado');
            }
            onSaved?.();
        } catch (er) {
            if (er?.status === 409) toast.error('Ya existe un usuario con ese correo');
            else if (er?.status !== 403) toast.error(er?.message || 'No se pudo guardar el usuario'); // 403 lo avisa el interceptor
        }
    };

    return (
        <div className="usr-modal" role="dialog" aria-modal="true" aria-label={editing ? 'Editar usuario' : 'Nuevo usuario'}>
            <div className="usr-modal__overlay" onClick={onClose} aria-hidden="true" />
            <div className="usr-modal__card" ref={cardRef}>
                <header className="usr-modal__head">
                    <div>
                        <h2 className="usr-modal__title">{editing ? 'Editar usuario' : 'Nuevo usuario'}</h2>
                        <span className="usr-modal__sub">{editing ? `#${user.id} · ${user.username || user.email}` : 'Usuario interno del staff.'}</span>
                    </div>
                    <button type="button" className="usr-modal__close" onClick={onClose} aria-label="Cerrar"><IconClose /></button>
                </header>

                <div className="usr-form">
                    <label className="usr-form__field">
                        <span className="usr-form__label">Nombre</span>
                        <input type="text" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
                    </label>
                    <label className="usr-form__field">
                        <span className="usr-form__label">Correo</span>
                        <input type="email" maxLength={180} value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} placeholder="usuario@dominio.co" />
                    </label>

                    {!editing && (
                        <div className="usr-form__row">
                            <label className="usr-form__field">
                                <span className="usr-form__label">Contraseña <em className="usr-form__hint">(10–100)</em></span>
                                <input type="password" minLength={10} maxLength={100} value={password} onChange={(e) => setPassword(e.target.value)} disabled={busy} autoComplete="new-password" />
                            </label>
                            <label className="usr-form__field">
                                <span className="usr-form__label">Rol</span>
                                <select value={role} onChange={(e) => setRole(e.target.value)} disabled={busy}>
                                    {roles.map((r) => <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>)}
                                </select>
                            </label>
                        </div>
                    )}

                    <label className="usr-form__field usr-form__field--half">
                        <span className="usr-form__label">Estado</span>
                        <select value={status} onChange={(e) => setStatus(Number(e.target.value))} disabled={busy}>
                            <option value={2}>Activo</option>
                            <option value={1}>Inactivo</option>
                        </select>
                    </label>

                    {editing && <p className="usr-form__note">El rol se cambia con “Cambiar rol”; la contraseña la restablece el usuario.</p>}
                </div>

                <footer className="usr-modal__foot">
                    <button type="button" className="usr-btn usr-btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
                    <button type="button" className="usr-btn usr-btn--primary" onClick={submit} disabled={busy || !name.trim() || !email.trim()}>{busy ? 'Guardando…' : editing ? 'Guardar' : 'Crear usuario'}</button>
                </footer>
            </div>
        </div>
    );
}
